import { TrendingDown, TrendingUp } from 'lucide-react';
import {
  getScoreColorVar,
  getScoreLabel,
} from '../../../../helpers/postScoreAndMetrics.helpers';
import {
  formatScore,
  UNSCORED_PLACEHOLDER,
} from '../../../../helpers/scoreDisplay';

interface Delta {
  change: number;
  change_direction: 'up' | 'down' | 'unchanged';
}

interface Props {
  score: number | null;
  replyCount: number;
  delta: Delta | null;
}

const R = 52;
const CIRC = 2 * Math.PI * R;

export default function ScoreHero({ score, replyCount, delta }: Props) {
  const clamped =
    typeof score === 'number' && Number.isFinite(score)
      ? Math.max(0, Math.min(100, score))
      : null;
  // Empty ring (no progress arc) when there's nothing to show, instead of
  // NaN geometry from dividing by a null score.
  const offset = clamped === null ? CIRC : CIRC * (1 - clamped / 100);
  const color =
    clamped === null ? 'var(--text-muted)' : getScoreColorVar(clamped);
  const showDelta =
    delta && delta.change_direction !== 'unchanged' && delta.change !== 0;

  // Renders inner content only — SectionShell supplies the `score` anchor +
  // the auto-numbered "Overall score" eyebrow (single source of truth).
  return (
    <div
      className={`POST__ANALYSIS__hero${
        showDelta ? ` flash-${delta!.change_direction}` : ''
      }`}
    >
      <div className="POST__ANALYSIS__hero__gauge">
        <svg
          viewBox="0 0 120 120"
          width="160"
          height="160"
          role="img"
          /* TRIL-669 (review F6): the label must announce EXACTLY what the ring
             renders. It used to round ("84") while the text showed one decimal
             ("84.0"). Both now read formatScore(clamped) — and the unscored case
             announces a state rather than a number. */
          aria-label={
            clamped === null
              ? 'Score not yet available'
              : `Score ${formatScore(clamped)} of 100`
          }
        >
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="var(--border-subtle)"
            strokeWidth="8"
          />
          {clamped !== null && (
            <circle
              cx="60"
              cy="60"
              r={R}
              fill="none"
              stroke={color}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={offset}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          )}
          <text
            x="60"
            y="60"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={clamped === null ? '24' : '30'}
            fontWeight="700"
            fill={
              clamped === null ? 'var(--text-muted)' : 'var(--text-primary)'
            }
          >
            {clamped === null ? UNSCORED_PLACEHOLDER : formatScore(clamped)}
          </text>
          {clamped !== null && (
            <text
              x="60"
              y="80"
              textAnchor="middle"
              fontSize="9"
              fill="var(--text-muted)"
            >
              / 100
            </text>
          )}
        </svg>
      </div>

      <div className="POST__ANALYSIS__hero__meta">
        <h2 className="POST__ANALYSIS__hero__label" style={{ color }}>
          {clamped === null ? 'Not yet scored' : getScoreLabel(clamped)}
        </h2>
        <div className="POST__ANALYSIS__hero__facts">
          <span>
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'} factored
          </span>
          <span className="POST__ANALYSIS__live">
            <span className="live-dot" /> Live
          </span>
        </div>
        {showDelta && (
          <div
            className={`POST__ANALYSIS__hero__delta delta-${delta!.change_direction}`}
          >
            {delta!.change_direction === 'up' ? (
              <TrendingUp size={16} />
            ) : (
              <TrendingDown size={16} />
            )}
            <span>
              {delta!.change > 0 ? '+' : ''}
              {delta!.change.toFixed(1)}
            </span>{' '}
            pts
          </div>
        )}
      </div>
    </div>
  );
}
